import { useEffect, useState } from "react"
import { useParams, Link } from "react-router"
import { fetchAnalytics } from "../lib/api"
import useUpgrade from "../hooks/useUpgrade"
import AlertRuleEditor from "./alerts/AlertRuleEditor"
import {
  Icon, fmt, axisLabels, timeAgo, countryRows, deviceRows, BotNote,
  ChartCard, AnStat, AreaChart, BarRows, Hours, LockedPanel,
} from "./analytics/charts"

// Downloads are free on every plan, so a locked page still offers them. The
// PNG URL is the SVG URL with the extension swapped — same derivation as
// HeroResult / DashboardCreator / QrPreviewModal, which each carry their own
// copy of this two-liner.
function pngFor(imageUrl) {
  if (!imageUrl) return null
  return imageUrl.replace(/\.svg(\?.*)?$/, ".png$1")
}

// Per-code analytics view at /app/codes/:token.
// Shape from `Analytics::CodeReport`; paywall behaviour from
// `docs/specs/2026-09-12-analytics-entitlement-enforcement.md`.
//
// Free user × code with > 50 scans: the server omits every detail section, so
// the page drops the gated tiles and charts entirely and renders one
// LockedPanel saying what's saved and what unlocks. Total scans keeps its
// card — it's the one number that survives the gate — and the header keeps
// the copy-link and PNG/SVG downloads, which are free on every plan and are
// half of why anyone opens a code's page. A paywalled page should still be a
// useful page.
//
// Every read of `data.charts` is guarded: with the gate on (AnalyticsGate)
// the key is absent rather than zeroed. Charts render only when present AND
// unlocked, so a locked user sees the same panel whether the flag is on (data
// withheld) or off (data present but not rendered) — one lock state, not two.

export default function CodeAnalyticsPage() {
  const { token } = useParams()
  const [status, setStatus] = useState("loading") // 'loading' | 'ready' | 'error'
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const { upgrade } = useUpgrade()

  useEffect(() => {
    let cancelled = false
    setStatus("loading")
    fetchAnalytics(token)
      .then((payload) => {
        if (cancelled) return
        setData(payload)
        setStatus("ready")
      })
      .catch((e) => {
        if (cancelled) return
        setError(e)
        setStatus("error")
      })
    return () => { cancelled = true }
  }, [token])

  if (status === "loading") {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4 text-center text-[color:var(--color-muted)] dark:text-muted-light text-sm">
        Loading analytics…
      </div>
    )
  }

  if (status === "error") {
    const notFound = error?.kind === "not_found"
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="border border-fail rounded p-4 text-fail text-sm">
          {notFound
            ? "We couldn't find that code in your account."
            : (error?.message || "Couldn't load analytics for this code.")}
        </div>
        <Link to="/app" className="mt-4 inline-block text-sm text-voltage hover:underline">
          ← Back to your codes
        </Link>
      </div>
    )
  }

  const locked = !!data.paywall?.locked
  const charts = data.charts
  // Render whatever the server sent, rather than keying off `locked`. With
  // AnalyticsGate off the payload is complete even for a locked code, and the
  // flag has to be able to put that data back in front of users without a
  // deploy — that is the entire reason it exists, since this slice is the one
  // change that removes something existing free users can see. Keying this off
  // the entitlement made the reduction ship unflagged.
  const showCharts = !!charts
  const created = data.created_at
    ? new Date(data.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <Link to="/app" className="inline-flex items-center gap-1.5 mb-4 text-sm text-voltage hover:underline">
        <Icon name="arrowLeft" size={16} /> Your codes
      </Link>

      <CodeHeader code={data} locked={locked} />

      {showCharts && <StatStrip data={data} />}

      {showCharts ? (
        <div className="flex flex-col gap-3">
          <ChartCard
            title="Scans over time · last 30 days"
            right={<span className="font-mono text-sm text-ink dark:text-cloud">{fmt(seriesTotal(charts.scans_over_time))}</span>}
          >
            <AreaChart series={charts.scans_over_time.map((p) => p.count)} labels={axisLabels(charts.scans_over_time)} height={150} />
          </ChartCard>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ChartCard title="By country">
              <BarRows rows={countryRows(charts.by_country, { limit: 5, withOther: true })} />
            </ChartCard>
            <ChartCard title="By device">
              <BarRows rows={deviceRows(charts.by_device)} />
              <BotNote rows={charts.by_device} />
            </ChartCard>
            <ChartCard title="Time of day · UTC">
              <Hours buckets={charts.by_hour} />
            </ChartCard>
          </div>
        </div>
      ) : (
        <LockedPanel
          header={<TotalScansHero data={data} />}
          lead={
            `Every one of them is recorded and waiting` +
            (created ? `, back to ${created}` : "") + "."
          }
          items={[
            "Daily scans, last 30 days",
            "Countries",
            "Devices",
            "Time of day",
            "Unique devices",
            "Last scan time",
          ]}
          onUpgrade={upgrade}
        />
      )}

      <CodeAlertsCard token={data.token} />
    </div>
  )
}

// Email alerts for this code (#304). Free on every plan, and shown on a
// locked page too: the total is never locked, so "this code got to N" shows
// nothing the gate holds back. The editor renders nothing (no empty card)
// while the email is off.
function CodeAlertsCard({ token }) {
  return (
    <AlertRuleEditor
      target={{ token }}
      wrap={(content) => (
        <div className="mt-3 border border-[color:var(--color-border)] dark:border-storm rounded-lg bg-white dark:bg-midnight p-4">
          <div className="text-xs uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light mb-2">
            Email alerts
          </div>
          {content}
        </div>
      )}
    />
  )
}

const seriesTotal = (points) => points.reduce((s, p) => s + p.count, 0)

// ---- Identity header --------------------------------------------------
//
// QR thumbnail + name + short link (+ Locked badge when paywalled) + destination, and
// a right-aligned meta column: created date and a live "last scan" pulse. The
// meta column hides under 560px.

function CodeHeader({ code, locked }) {
  const shortLinkLabel = (code.short_url || "").replace(/^https?:\/\//, "")
  const created = code.created_at ? new Date(code.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null
  const lastScan = timeAgo(code.last_scan_at)
  return (
    <div className="flex items-start gap-3 pb-4 mb-4 border-b border-[color:var(--color-border)] dark:border-storm">
      <img
        src={code.image_url}
        alt=""
        aria-hidden="true"
        className="w-[52px] h-[52px] border border-[color:var(--color-border)] dark:border-storm rounded bg-white flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        {/* The code's name (#326): its label, else one read from its link. */}
        <h1 className="text-lg font-semibold leading-snug text-ink dark:text-cloud truncate" title={code.display_label}>
          {code.display_label || shortLinkLabel}
        </h1>
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm text-voltage truncate">{shortLinkLabel}</span>
          {locked && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-plasma dark:text-plasma-light border border-plasma rounded px-1.5 py-0.5 flex-shrink-0">
              <Icon name="lock" size={11} />Locked
            </span>
          )}
        </div>
        <div className="font-mono text-sm text-ink dark:text-cloud truncate mt-0.5">→ {code.destination_url}</div>
        <CodeLinkActions code={code} />
      </div>
      <div className="hidden min-[560px]:flex flex-col items-end gap-1 text-xs text-[color:var(--color-muted)] dark:text-muted-light flex-shrink-0">
        {created && <span>Created {created}</span>}
        {lastScan && (
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-voltage animate-pulse" aria-hidden="true" />
            last scan {lastScan}
          </span>
        )}
      </div>
    </div>
  )
}

// ---- Code actions (free on every plan) --------------------------------
//
// Copy the short link, download the image. Neither is gated by the scan
// paywall, so these stay on a locked page — without them a paywalled code
// page offers the visitor nothing at all to do.

function CodeLinkActions({ code }) {
  const [copied, setCopied] = useState(false)

  function copyShortLink() {
    navigator.clipboard?.writeText(code.short_url || `tgo.sh/${code.token}`).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1400)
  }

  const btn =
    "inline-flex items-center gap-1.5 h-8 px-2.5 rounded border text-xs font-semibold transition " +
    "border-[color:var(--color-border)] dark:border-storm text-[color:var(--color-muted)] dark:text-muted-light " +
    "hover:text-coal dark:hover:text-cloud hover:border-voltage hover:bg-voltage-pale dark:hover:bg-voltage/15 " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"

  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      <button type="button" onClick={copyShortLink} className={btn + " bg-transparent cursor-pointer"}>
        <Icon name={copied ? "check" : "copy"} size={13} />
        {copied ? "Copied" : "Copy link"}
      </button>
      <a href={pngFor(code.image_url)} download={`tango-qr-${code.token}.png`} className={btn}>
        <Icon name="download" size={13} /> PNG
      </a>
      <a href={code.image_url} download={`tango-qr-${code.token}.svg`} className={btn}>
        <Icon name="download" size={13} /> SVG
      </a>
    </div>
  )
}

// ---- Locked hero: the one number that survives the gate ---------------
//
// Rendered inside LockedPanel rather than as its own tile. It reads as the
// top half of one card — what you have — above the hairline, with what's
// gated below it.

function TotalScansHero({ data }) {
  const total = data.total_scans
  const threshold = data.paywall?.threshold ?? 50
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light">
        Total scans
      </div>
      <div className="font-display font-semibold text-4xl sm:text-5xl leading-none text-voltage-dark dark:text-voltage-light mt-2">
        {fmt(total)}
      </div>
      <div className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-2">
        past the free {threshold}-scan window · still counting
      </div>
    </div>
  )
}

// ---- Headline stat strip (unlocked only) ------------------------------
//
// Total scans, Unique devices, Top country. The locked view doesn't render
// this at all: the two gated tiles are dropped rather than dashed out, and
// the surviving total moves into LockedPanel's header.

function StatStrip({ data }) {
  const top = data.top_country
  const total = data.total_scans
  const threshold = data.paywall?.threshold ?? 50
  // `applies` is false on Pro: there's no window to count down or be past.
  const totalSub = data.paywall?.applies === false
    ? "lifetime · no scan limit"
    : total > threshold
      ? `past the free ${threshold}-scan window`
      : `${threshold - total} left in free window`

  // A hosted contact page's "Save contact" taps (#332). Present only for a
  // code that opens its page, and only when the gate let it through.
  const saves = typeof data.contact_saves === "number" ? data.contact_saves : null

  return (
    <div className={"grid grid-cols-1 gap-3 mb-3 " + (saves === null ? "sm:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4")}>
      <AnStat eyebrow="Total scans" value={fmt(total)} sub={totalSub} />
      <AnStat eyebrow="Unique devices" value={fmt(data.unique_devices)} sub="distinct devices" />
      <AnStat
        eyebrow="Top country"
        value={top ? top.code : "—"}
        sub={top ? `${fmt(top.scans)} scans` : null}
      />
      {saves !== null && (
        <AnStat eyebrow="Saved to contacts" value={fmt(saves)} sub="taps on Save contact" />
      )}
    </div>
  )
}
